import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiPlus, FiEdit2, FiTrash2, FiExternalLink, FiSearch,
  FiToggleLeft, FiToggleRight, FiMoreVertical
} from 'react-icons/fi';
import { AdminLayout } from '../../components/layout';
import { Card, Button, Loading, ConfirmDialog, AlertModal } from '../../components/common';
import { getAllCompanies, toggleCompanyStatus, deleteCompany } from '../../firebase/companies';
import { getCarStats } from '../../firebase/cars';
import { getBookingStats } from '../../firebase/bookings';

const CompanyList = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null, title: '', type: 'warning' });
  const [alert, setAlert] = useState({ isOpen: false, type: 'success', title: '', message: '' });

  // Helper function to get company URL based on environment
  const getCompanyUrl = (slug) => {
    const hostname = window.location.hostname;
    const port = window.location.port;
    
    if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
      return `http://${slug}.localhost:${port || '3000'}`;
    }
    
    return `https://${slug}.carbooking.com`;
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const result = await getAllCompanies();
      
      // Load stats for each company
      const companiesWithStats = await Promise.all(
        (result.companies || []).map(async (company) => {
          const [carStats, bookingStats] = await Promise.all([
            getCarStats(company.id),
            getBookingStats(company.id)
          ]);
          return {
            ...company,
            carCount: carStats.stats?.total || 0,
            bookingCount: bookingStats.stats?.total || 0,
            revenue: bookingStats.stats?.totalRevenue || 0
          };
        })
      );
      
      setCompanies(companiesWithStats);
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (company) => {
    const newStatus = company.status === 'active' ? 'inactive' : 'active';
    const result = await toggleCompanyStatus(company.id, newStatus);
    
    if (!result.error) {
      setCompanies(companies.map(c => 
        c.id === company.id ? { ...c, status: newStatus } : c
      ));
    }
    setActiveDropdown(null);
  };

  const handleDelete = async (company) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Company',
      message: `Are you sure you want to delete "${company.name}"? This action cannot be undone and will permanently remove the company from the database.`,
      type: 'danger',
      onConfirm: async () => {
        try {
          const result = await deleteCompany(company.id);
          
          if (result.error) {
            setAlert({
              isOpen: true,
              type: 'error',
              title: 'Delete Failed',
              message: result.error || 'Failed to delete company. Please try again.'
            });
            console.error('Delete error:', result.error);
          } else {
            // Remove from local state
            setCompanies(companies.filter(c => c.id !== company.id));
            // Reload companies to ensure consistency
            await loadCompanies();
            setAlert({
              isOpen: true,
              type: 'success',
              title: 'Company Deleted',
              message: `"${company.name}" has been permanently deleted.`
            });
          }
        } catch (error) {
          console.error('Error deleting company:', error);
          setAlert({
            isOpen: true,
            type: 'error',
            title: 'Delete Failed',
            message: error.message || 'Failed to delete company. Please try again.'
          });
        } finally {
          setActiveDropdown(null);
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        }
      }
    });
  };

  // Filter companies
  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.location?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || company.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <AdminLayout title="Companies">
        <div className="flex items-center justify-center h-64">
          <Loading />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Companies">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">All Companies</h2>
          <p className="text-gray-600">{companies.length} total companies</p>
        </div>
        <Link to="/admin/companies/new">
          <Button className="flex items-center gap-2">
            <FiPlus size={18} />
            Add Company
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by name, slug, or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </Card>

      {/* Companies Grid */}
      {filteredCompanies.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCompanies.map((company) => (
            <Card key={company.id} className="overflow-hidden">
              {/* Company Header */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                      {company.logo ? (
                        <img src={company.logo} alt={company.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xl font-bold text-gray-400">
                          {company.name[0]}
                        </span>
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{company.name}</h3>
                      <a 
                        href={getCompanyUrl(company.slug)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary-600 hover:underline flex items-center gap-1"
                      >
                        {company.slug}.carbooking.com
                        <FiExternalLink size={12} />
                      </a>
                    </div>
                  </div>
                  
                  {/* Actions Dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === company.id ? null : company.id)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <FiMoreVertical size={18} />
                    </button>
                    
                    {activeDropdown === company.id && (
                      <>
                        <div 
                          className="fixed inset-0 z-10" 
                          onClick={() => setActiveDropdown(null)}
                        />
                        <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                          <Link
                            to={`/admin/companies/${company.id}`}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <FiEdit2 size={14} />
                            Edit Company
                          </Link>
                          <button
                            onClick={() => handleToggleStatus(company)}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                          >
                            {company.status === 'active' ? (
                              <>
                                <FiToggleLeft size={14} />
                                Deactivate
                              </>
                            ) : (
                              <>
                                <FiToggleRight size={14} />
                                Activate
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleDelete(company)}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
                          >
                            <FiTrash2 size={14} />
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Company Info */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    company.status === 'active' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {company.status}
                  </span>
                  {company.location && (
                    <span className="text-sm text-gray-500">{company.location}</span>
                  )}
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-gray-50 rounded-lg">
                    <p className="text-lg font-semibold text-gray-900">{company.carCount}</p>
                    <p className="text-xs text-gray-500">Cars</p>
                  </div>
                  <div className="p-2 bg-gray-50 rounded-lg">
                    <p className="text-lg font-semibold text-gray-900">{company.bookingCount}</p>
                    <p className="text-xs text-gray-500">Bookings</p>
                  </div>
                  <div className="p-2 bg-gray-50 rounded-lg">
                    <p className="text-lg font-semibold text-gray-900">₹{(company.revenue / 1000).toFixed(0)}k</p>
                    <p className="text-xs text-gray-500">Revenue</p>
                  </div>
                </div>
                
                {/* Contact */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-600 truncate">{company.email}</p>
                  <p className="text-sm text-gray-600">{company.phone}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12 text-center">
          <div className="text-gray-400 mb-4">
            <FiSearch size={48} className="mx-auto" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchQuery || statusFilter !== 'all' ? 'No companies found' : 'No companies yet'}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchQuery || statusFilter !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Start by adding your first company to the platform'
            }
          </p>
          {!searchQuery && statusFilter === 'all' && (
            <Link to="/admin/companies/new">
              <Button>Add First Company</Button>
            </Link>
          )}
        </Card>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        type={alert.type}
        title={alert.title}
        message={alert.message}
      />
    </AdminLayout>
  );
};

export default CompanyList;
